import { WeaveButton } from '../UI/WeaveButton/WeaveButton';
import { useDailySunlightStore } from '../../store/dailySunlightsStore';
import { DailySunlightResult } from '../../types/sunlight';
import { MetricSelector } from './MetricSelector';
import styles from './styles.module.scss';
import { Select } from '../UI/Select/Select';
import { useEffect, useState } from 'react';

interface DailySunlightControlsProps {
	calculateDailySunlightForRooms: (
		selectedFloorIndex?: number | null
	) => Promise<DailySunlightResult[] | undefined>;
	calculateDailySunlightForSelectedGridPoints: () => Promise<DailySunlightResult[] | undefined>;
	availableFloorIndices: number[];
	calculatingDailySunlight: boolean;
	onMetricChange: (metric: keyof DailySunlightResult) => void;
	disabled?: boolean;
}

export const DailySunlightControls = (props: DailySunlightControlsProps) => {
	const {
		calculateDailySunlightForRooms,
		calculateDailySunlightForSelectedGridPoints,
		availableFloorIndices,
		calculatingDailySunlight,
		onMetricChange,
		disabled = false,
	} = props;
	const [selectedFloor, setSelectedFloor] = useState<string>('all');

	useEffect(() => {
		if (selectedFloor === 'all') {
			return;
		}

		const selectedFloorIndex = Number(selectedFloor);
		if (!availableFloorIndices.includes(selectedFloorIndex)) {
			setSelectedFloor('all');
		}
	}, [availableFloorIndices, selectedFloor]);

	const { currentSelectedMetric, setCurrentSelectedMetric, selectedGridPoints } = useDailySunlightStore();
	const sunlightResultsRooms = useDailySunlightStore((state) => state.sunlightResultsRooms);
	const setSunlightResultsRooms = useDailySunlightStore((state) => state.setSunlightResultsRooms);
	const setSunlightResultsGridPoints = useDailySunlightStore(
		(state) => state.setSunlightResultsGridPoints
	);

	const handleDailySunlightCalculation = async () => {
		setSunlightResultsRooms(null);
		const floorIndex = selectedFloor === 'all' ? null : Number(selectedFloor);
		const roomResult = await calculateDailySunlightForRooms(floorIndex);
		setSunlightResultsRooms(roomResult || null);
	};

	const handleDailySunlightCalculationForGridPoints = async () => {
		const gridPointResult = await calculateDailySunlightForSelectedGridPoints();
		if (!gridPointResult) return;
		setSunlightResultsGridPoints(gridPointResult);
	};

	const handleMetricChange = (metric: keyof DailySunlightResult) => {
		setCurrentSelectedMetric(metric);
		onMetricChange(metric);
	};

	return (
		<div>
			<div className={styles.controlsRow}>
				<WeaveButton
					onClick={handleDailySunlightCalculation}
					disabled={calculatingDailySunlight || disabled}
					variant="solid"
				>
					Daily Sunlight Analysis
				</WeaveButton>
				<Select
					value={selectedFloor}
					onChange={(event) => setSelectedFloor(event.detail.value)}
					disabled={calculatingDailySunlight || disabled}
					options={[
						{ label: 'All floors', value: 'all' },
						...availableFloorIndices
							.slice()
							.sort((a, b) => a - b)
							.map((floorIndex) => ({
								label: `Floor ${floorIndex}`,
								value: String(floorIndex),
							})),
					]}
				/>
				{selectedGridPoints.length > 2 && (
					<WeaveButton
						onClick={handleDailySunlightCalculationForGridPoints}
						disabled={calculatingDailySunlight || disabled}
						variant="solid"
					>
						Detailed sunlight for selected grid points
					</WeaveButton>
				)}
				<MetricSelector
					onMetricChange={handleMetricChange}
					currentMetric={currentSelectedMetric}
					disabled={calculatingDailySunlight || !sunlightResultsRooms}
				/>
			</div>
		</div>
	);
};
